<template>
    <SettingsLayout>
        <div class="px-4 pt-6 sm:px-6">
            <Tabs v-model="activeTab" class="w-full">
                <TabsList class="mb-6">
                    <TabsTrigger value="sending">
                        {{ $t("domains.tabs.sending") }}
                    </TabsTrigger>
                    <TabsTrigger value="page">
                        {{ $t("domains.tabs.page") }}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="sending">
                    <SettingsSubHeader v-if="domains.length > 0">
                        <template #title> {{ $t("domains.title") }} </template>
                        <template #description>
                            {{ $t("domains.description") }}</template
                        >
                        <template #actions>
                            <AddDomain>
                                <AddButton
                                    :paid-only="true"
                                    feature="custom_domain"
                                    data-cy="add-sending-domain"
                                >
                                    {{ $t("domains.add_domain") }}</AddButton
                                >
                            </AddDomain>
                        </template>
                    </SettingsSubHeader>

                    <div class="pt-4">
                        <template v-if="domains.length > 0">
                            <Domain
                                v-for="domain in domains"
                                :key="domain.id"
                                :domain="domain"
                            />
                        </template>

                        <template v-else>
                            <EmptyStateWithIcon
                                class="mt-9"
                                :title="$t('domains.empty_state.title')"
                                :description="
                                    $t('domains.empty_state.description')
                                "
                            >
                                <template #icon>
                                    <svg
                                        width="76"
                                        height="76"
                                        viewBox="0 0 76 76"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clip-path="url(#clip0_2290_17222)">
                                            <path
                                                d="M64.0843 32.4572C67.1463 46.863 57.9504 61.0234 43.5446 64.0855M64.0843 32.4572C61.0222 18.0515 46.8618 8.85556 32.456 11.9176M64.0843 32.4572L11.9164 43.5459M43.5446 64.0855C29.1389 67.1475 14.9784 57.9516 11.9164 43.5459M43.5446 64.0855C35.3521 58.4765 29.631 49.931 27.5667 40.2193C25.5025 30.5075 27.2531 20.3739 32.456 11.9176M43.5446 64.0855C48.7475 55.6292 50.4982 45.4955 48.4339 35.7838C46.3696 26.0721 40.6486 17.5266 32.456 11.9176M11.9164 43.5459C8.85434 29.1401 18.0502 14.9796 32.456 11.9176"
                                                stroke="url(#paint0_linear_2290_17222)"
                                                stroke-width="4"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_2290_17222"
                                                x1="13.1464"
                                                y1="21.4757"
                                                x2="62.0787"
                                                y2="55.9514"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF8D47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                            <clipPath id="clip0_2290_17222">
                                                <rect
                                                    width="64"
                                                    height="64"
                                                    fill="white"
                                                    transform="translate(0.0461426 13.3525) rotate(-12)"
                                                />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </template>
                            </EmptyStateWithIcon>

                            <div class="mt-8 flex justify-center">
                                <AddDomain>
                                    <AddButton
                                        class="flex"
                                        :paid-only="true"
                                        feature="custom_domain"
                                        data-cy="create-sending-domain"
                                    >
                                        {{
                                            $t("domains.add_domain")
                                        }}</AddButton
                                    >
                                </AddDomain>
                            </div>
                        </template>
                    </div>
                </TabsContent>

                <TabsContent value="page">
                    <SettingsSubHeader v-if="pageDomains.length > 0">
                        <template #title>
                            {{ $t("page_domains.title") }}
                        </template>
                        <template #description>
                            {{ $t("page_domains.description") }}</template
                        >
                        <template #actions>
                            <ManagePageDomain>
                                <AddButton
                                    :paid-only="true"
                                    feature="page_domains"
                                    data-cy="add-page-domain"
                                >
                                    {{ $t("page_domains.add") }}</AddButton
                                >
                            </ManagePageDomain>
                        </template>
                    </SettingsSubHeader>

                    <div class="mt-8 px-6">
                        <template v-if="pageDomains.length > 0">
                            <!-- Personal domains section -->
                            <section
                                v-if="personalPageDomains.length > 0"
                                class="mb-10"
                            >
                                <header class="mb-4 gap-2 flex items-center">
                                    <User :size="16" class="text-iron-400" />
                                    <h3
                                        class="font-medium text-iron-600 text-[13px]"
                                    >
                                        {{ $t("page_domains.personal_domain") }}
                                    </h3>
                                    <div class="bg-iron-100 h-px flex-1"></div>
                                </header>
                                <div class="space-y-3">
                                    <PageDomain
                                        v-for="pageDomain in personalPageDomains"
                                        :key="pageDomain.id"
                                        :page-domain="pageDomain"
                                    />
                                </div>
                            </section>

                            <!-- Team domains sections -->
                            <section
                                v-for="group in teamPageDomainsGrouped"
                                :key="group.teamId"
                                class="mb-10"
                            >
                                <header class="mb-4 gap-2 flex items-center">
                                    <Users :size="16" class="text-iron-400" />
                                    <h3
                                        class="font-medium text-iron-600 text-[13px]"
                                    >
                                        {{ group.teamName }}
                                    </h3>
                                    <div class="bg-iron-100 h-px flex-1"></div>
                                </header>
                                <div class="space-y-3">
                                    <PageDomain
                                        v-for="pageDomain in group.domains"
                                        :key="pageDomain.id"
                                        :page-domain="pageDomain"
                                    />
                                </div>
                            </section>
                        </template>

                        <template v-else>
                            <EmptyStateWithIcon
                                class="mt-9"
                                :title="$t('page_domains.empty_state.title')"
                                :description="
                                    $t('page_domains.empty_state.description')
                                "
                            >
                                <template #icon>
                                    <svg
                                        width="76"
                                        height="76"
                                        viewBox="0 0 76 76"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M38 59C49.598 59 59 49.598 59 38C59 26.402 49.598 17 38 17C26.402 17 17 26.402 17 38C17 49.598 26.402 59 38 59Z"
                                            stroke="url(#paint0_linear_page_domains_empty)"
                                            stroke-width="4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M17 38H59"
                                            stroke="url(#paint1_linear_page_domains_empty)"
                                            stroke-width="4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M38 17C42.4183 22.0974 44.9301 28.4696 45.04 35.12C44.9301 41.7704 42.4183 48.1426 38 53.24C33.5817 48.1426 31.0699 41.7704 30.96 35.12C31.0699 28.4696 33.5817 22.0974 38 17Z"
                                            stroke="url(#paint2_linear_page_domains_empty)"
                                            stroke-width="4"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_page_domains_empty"
                                                x1="17"
                                                y1="17"
                                                x2="59"
                                                y2="59"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF8D47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_page_domains_empty"
                                                x1="17"
                                                y1="38"
                                                x2="59"
                                                y2="38"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF8D47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint2_linear_page_domains_empty"
                                                x1="30.96"
                                                y1="17"
                                                x2="45.04"
                                                y2="53.24"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stop-color="#FF8D47" />
                                                <stop
                                                    offset="0.147634"
                                                    stop-color="#FF7447"
                                                />
                                                <stop
                                                    offset="0.454086"
                                                    stop-color="#FF4247"
                                                />
                                                <stop
                                                    offset="0.636926"
                                                    stop-color="#E92A5B"
                                                />
                                                <stop
                                                    offset="0.740367"
                                                    stop-color="#DD1C67"
                                                />
                                                <stop
                                                    offset="0.842991"
                                                    stop-color="#D31071"
                                                />
                                                <stop
                                                    offset="1"
                                                    stop-color="#CF0283"
                                                />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </template>
                            </EmptyStateWithIcon>

                            <div class="mt-8 flex justify-center">
                                <ManagePageDomain>
                                    <AddButton
                                        class="flex"
                                        :paid-only="true"
                                        feature="page_domains"
                                        data-cy="create-page-domain"
                                    >
                                        {{ $t("page_domains.add") }}</AddButton
                                    >
                                </ManagePageDomain>
                            </div>
                        </template>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    </SettingsLayout>
</template>

<script lang="ts" setup>
import SettingsLayout from "@/Layouts/SettingsLayout.vue";
import SettingsSubHeader from "@/Layouts/components/SettingsSubHeader.vue";
import AddButton from "../Atomic/AddButton.vue";
import AddDomain from "@/Pages/Zaptime/App/Domains/components/AddDomain.vue";
import Domain from "@/Pages/Zaptime/App/Domains/components/Domain.vue";
import PageDomain from "@/Pages/Zaptime/App/Domains/components/PageDomain.vue";
import ManagePageDomain from "@/Pages/Zaptime/App/Domains/components/ManagePageDomain.vue";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Users } from "lucide-vue-next";
import { ref, computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import type { PageDomainDTO } from "@/types/PageDomains";

interface SendingDomain {
    id: number;
    name: string;
    verified: boolean;
    dkim: boolean;
}

interface TeamDomainsGroup {
    teamId: number;
    teamName: string;
    domains: PageDomainDTO[];
}

const props = defineProps<{
    domains: SendingDomain[];
    pageDomains: PageDomainDTO[];
}>();

const page = usePage();
const activeTab = ref(page.url.includes("tab=page") ? "page" : "sending");
const selectedTeamId = Number(
    new URLSearchParams(page.url.split("?")[1] ?? "").get("team"),
);

// Personal page domains (from personal team)
const personalPageDomains = computed(() => {
    return props.pageDomains.filter((pd) => pd.is_personal_team === true);
});

// Team page domains grouped by team (excluding personal)
const teamPageDomainsGrouped = computed((): TeamDomainsGroup[] => {
    const nonPersonalDomains = props.pageDomains.filter(
        (pd) => pd.is_personal_team !== true,
    );

    const grouped = new Map<number, TeamDomainsGroup>();

    for (const domain of nonPersonalDomains) {
        if (!grouped.has(domain.team_id)) {
            grouped.set(domain.team_id, {
                teamId: domain.team_id,
                teamName: domain.team_name ?? `Team ${domain.team_id}`,
                domains: [],
            });
        }
        grouped.get(domain.team_id)!.domains.push(domain);
    }

    return Array.from(grouped.values()).sort((first, second) => {
        if (first.teamId === selectedTeamId) return -1;
        if (second.teamId === selectedTeamId) return 1;

        return first.teamName.localeCompare(second.teamName);
    });
});
</script>
